import React from 'react';
import { ArrowRight, AlertCircle, CheckCircle2, X, BookHeart } from 'lucide-react';

interface ForgotPasswordFormProps {
  formData: { email: string };
  errors: Record<string, string>;
  loading: boolean;
  resetEmailSent: boolean;
  emailInputRef: React.RefObject<HTMLInputElement>;
  onFieldChange: (field: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitchMode: (mode: 'login' | 'register' | 'forgot-password') => void;
  onClose?: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  formData,
  errors,
  loading,
  resetEmailSent,
  emailInputRef,
  onFieldChange,
  onSubmit,
  onSwitchMode,
  onClose
}) => {
  const Header = (
    <>
      <div className="lg:hidden warm-panel relative pt-4 pb-3 px-5 shrink-0">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="absolute right-3 top-3 flex items-center justify-center text-stone-500 rounded-full bg-white/60 hover:bg-white/80 transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        )}
        <h2 id="auth-modal-title" className="font-display text-[22px] leading-tight text-stone-900 pr-10">
          Recuperar contraseña
        </h2>
        <p className="text-[12px] text-stone-600 mt-0.5">
          Te enviaremos un enlace por email
        </p>
      </div>
      <div className="hidden lg:flex lg:w-1/2 warm-panel relative overflow-hidden flex-col justify-center px-14">
        <div className="w-10 h-10 bg-rose-400 rounded-2xl flex items-center justify-center text-white shadow-lg mb-4">
          <BookHeart size={20} />
        </div>
        <h1 className="font-display text-[40px] leading-[1.05] text-stone-900">
          Recuperar contraseña
        </h1>
        <p className="text-sm text-stone-600 mt-4 max-w-[340px] leading-relaxed">
          Te enviaremos un enlace para restablecer tu contraseña.
        </p>
        <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-rose-200/40 blur-3xl pointer-events-none" />
      </div>
    </>
  );

  if (resetEmailSent) {
    return (
      <div className="h-full w-full flex flex-col lg:flex-row min-h-0">
        {Header}
        <div className="warm-panel lg:w-1/2 lg:bg-[#FEFCFA] flex-1 flex flex-col min-h-0 lg:px-12 lg:py-10 relative">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="hidden lg:flex items-center justify-center absolute top-10 right-10 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-full transition-colors disabled:opacity-50 touch-manipulation"
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          )}
          <div className="flex-1 flex flex-col min-h-0 px-3 pb-3 lg:px-0 lg:pb-0">
            <div className="flex-1 flex flex-col min-h-0 bg-white lg:bg-transparent rounded-2xl lg:rounded-none border lg:border-0 border-stone-200 shadow-sm lg:shadow-none p-4 lg:p-0">
              <div className="flex-1 flex flex-col justify-center text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-stone-900 mb-2">¡Correo enviado!</h3>
                <p className="text-sm text-stone-600">
                  Revisa tu correo electrónico y sigue las instrucciones para restablecer tu contraseña.
                </p>
              </div>
              <div className="pt-3 shrink-0">
                <button
                  type="button"
                  onClick={() => onSwitchMode('login')}
                  className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 shadow-lg shadow-rose-500/30 transition-all"
                >
                  Volver al inicio de sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="h-full w-full flex flex-col lg:flex-row min-h-0">
      {Header}
      <div className="warm-panel lg:w-1/2 lg:bg-[#FEFCFA] flex-1 flex flex-col min-h-0 lg:px-12 lg:py-10 relative">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="hidden lg:flex items-center justify-center absolute top-10 right-10 text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-full transition-colors disabled:opacity-50 touch-manipulation"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        )}
        <div className="hidden lg:block mb-5 shrink-0">
          <h2 className="text-xl font-semibold text-stone-900 mb-0.5">Recuperar contraseña</h2>
          <p className="text-xs text-stone-500">Te enviaremos un enlace por email</p>
        </div>
        <div className="flex-1 flex flex-col min-h-0 px-3 pb-3 lg:px-0 lg:pb-0">
          <div className="flex-1 flex flex-col min-h-0 bg-white lg:bg-transparent rounded-2xl lg:rounded-none border lg:border-0 border-stone-200 shadow-sm lg:shadow-none p-4 lg:p-0">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-3 p-0.5">
              <div>
                <label htmlFor="email" className="block text-[13px] font-medium text-stone-700 mb-1">
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => onFieldChange('email', e.target.value)}
                  className={`w-full h-11 text-sm px-3.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 placeholder:text-stone-400 transition-all duration-200 ${
                    errors.email ? 'border-rose-500' : 'border-stone-200'
                  }`}
                  placeholder="tu@email.com"
                  required
                  autoComplete="email"
                  inputMode="email"
                />
                {errors.email && (
                  <p className="mt-1 text-[12px] text-rose-600 flex items-center" role="alert">
                    <AlertCircle className="h-3 w-3 mr-0.5 shrink-0" />
                    {errors.email}
                  </p>
                )}
              </div>

              {errors.submit && (
                <div className="p-3 rounded-xl bg-rose-50 text-rose-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span className="text-xs lg:text-sm leading-snug">{errors.submit}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-3 space-y-2 shrink-0">
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-semibold text-sm flex items-center justify-center gap-1.5 shadow-lg shadow-rose-500/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                    Enviar enlace
                  </span>
                ) : (
                  <>
                    Enviar enlace <ArrowRight size={15} />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => onSwitchMode('login')}
                className="w-full text-[12px] lg:text-xs text-stone-600 hover:text-stone-900 transition-colors py-1"
              >
                Volver al inicio de sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
